import { Dispatch, SetStateAction, useEffect, useState } from 'react';

interface HookReturnValue<T> {
  undo?: VoidFunction;
  redo?: VoidFunction;
  value: T;
  history: T[];
  updateValue: Dispatch<SetStateAction<T>>;
}

// Providing an element to bind to will enable the ctrl/cmd + z/y bindings
const useUndoRedoValue = <T>(defaultValue: T): HookReturnValue<T> => {
  const [history, setHistory] = useState<T[]>([]);
  const [updatedValue, setUpdatedValue] = useState<T>(defaultValue);
  const [indexCounter, setIndexCounter] = useState(0);

  const updateCounter = (amount: number) => {
    const newIndex = indexCounter + amount;
    const clampedAmount =
      newIndex < 0
        ? 0
        : newIndex > history.length - 1
        ? history.length - 1
        : newIndex;
    setIndexCounter(clampedAmount);
  };

  useEffect(() => {
    if (updatedValue !== undefined) {
      const newHistory = [...history];
      if (indexCounter > 0) {
        newHistory.splice(0, indexCounter);
        setIndexCounter(0);
      }
      newHistory.unshift(updatedValue);
      setHistory(newHistory);
    }
  }, [updatedValue]);

  return {
    undo:
      indexCounter !== history.length - 1 ? () => updateCounter(1) : undefined,
    redo: indexCounter > 0 ? () => updateCounter(-1) : undefined,
    value: history[indexCounter] || updatedValue,
    history,
    updateValue: setUpdatedValue,
  };
};
export default useUndoRedoValue;
